import styled from 'styled-components'
import { colors } from 'src/Global/Styles/variables'
import { ArrowRightCircle } from 'react-feather'

import { CustomerCustomComponents } from '../customerCustomComponents'
import { InitialButton as DefaultInitialButton } from '../../../Buttons'
import {
  InfoContainer as DefaultInfoContainer,
  InitialButtonContainer as DefaultInitialButtonContainer,
  ModalContainer as DefaultModalContainer,
  PatientUploadContainer as DefaultPatientUploadContainer,
  PatientVerificationContainer as DefaultPatientVerificationContainer,
} from '../../../Containers'
import { HeaderContainer as DefaultHeaderContainer } from '../../../Headers/defaultComponents'
import {
  Layer as DefaultLayer,
  Modal as DefaultModal,
} from '../../../../../../Components/Common/ModalLayer/defaultComponents'
import {
  UploadBoxContainer as DefaultUploadBoxContainer,
  UploadButton as DefaultUploadButton,
  UploadFileInfo as DefaultUploadFileInfo,
  ImageCard as DefaultImageCard,
  StyledDeleteButton as DefaultStyledDeleteButton,
  ImageMetaInfo as DefaultImageMetaInfo,
} from '../../../Upload/defaultComponents'

/**
 * This file defines the components and styles that are specific to this customer.
 * These custom components are based on parent components, which are used by default.
 */

/**
 * Customer logo
 */
export { default as AstellasLogo } from './logo.svg'

/**
 * Common variables
 */
const titleLetterSpacing = '0.01em'
const textLetterSpacing = '0.02em'

/**
 * Button components
 */
const InitialButton = styled(DefaultInitialButton)`
  padding: 1rem;
  width: 100%;
  background-color: red;

  @media (min-width: 768px) {
    width: 30%;
  }

  ${(props) => {
    const commonStyle = `
    font-family: ${props.theme.mainFontTheme};
    letter-spacing: ${titleLetterSpacing};
    border-radius: 32px;
    font-size: 1.25rem;
    font-weight: 600;
  `
    const conditionalStyle = () =>
      props.filled
        ? `
      background-color: ${
        props.disabled
          ? 'rgba(171, 225, 255, 0.35)'
          : props.theme.mainThemeColor
      };
      color: ${props.disabled ? 'rgba(51, 51, 51, 0.35)' : '#333333'};
      `
        : `
      background-color: ${colors.white};
      color: #333333;
      border: 0.125rem solid #ABE1FF;
    `

    const hoverStyle = () =>
      !props.disabled
        ? ` 
        &:hover {
          transition: all 0.3s ease;
          color: ${colors.white};
          border-color: transparent;
          background-color: #005785;
        }
        
        &:hover svg {
          transition: color 0.3s ease;
          color: ${colors.white};
        }
      `
        : null

    return commonStyle + conditionalStyle() + hoverStyle()
  }};
`

const NextButton = styled(InitialButton)`
  width: 100%;
  max-width: 175px;

  @media (min-width: 375px) {
    max-width: 200px;
  }

  @media (min-width: 768px) {
    max-width: 300px;
  }
`

const BackButton = styled(InitialButton)`
  width: 100%;
  max-width: 100px;

  @media (min-width: 375px) {
    max-width: 125px;
  }

  @media (min-width: 768px) {
    max-width: 150px;
  }
`

const ResendButton = styled(InitialButton)`
  width: 100%;
  margin-top: 1rem;
`

const TakePhotoButton = styled(InitialButton)`
  width: 100%;
`

const ContinueIcon = styled(ArrowRightCircle)<{ disabled?: boolean }>`
  vertical-align: middle;
  ${(props) => `
    color: ${props.disabled ? 'rgba(51, 51, 51, 0.35)' : '#333333'};
    width: 1.5em;
    height: 1.5em;
    padding-left: 0.3em;
  `};
`

/**
 * Container components
 */
const InfoContainer = styled(DefaultInfoContainer)`
  h2 {
    font-weight: 600;
    letter-spacing: ${textLetterSpacing};
    line-height: 150%;
  }

  li {
    font-size: 1rem;
    letter-spacing: ${textLetterSpacing};
    line-height: 150%;
  }
`

const InitialButtonContainer = styled(DefaultInitialButtonContainer)`
  margin-top: 2rem;

  @media (min-width: 768px) {
    justify-content: end;
  }
`

const ModalContainer = styled(DefaultModalContainer)`
  h2 {
    font-weight: 600;
    letter-spacing: ${titleLetterSpacing};
    color: #333333;
    line-height: 130%;
  }

  p {
    color: ${colors.brightGrey};
    letter-spacing: ${textLetterSpacing};
    line-height: 150%;
  }
`

const PatientUploadContainer = styled(DefaultPatientUploadContainer)`
  padding: 1.5rem;
`

const PatientVerificationContainer = styled(
  DefaultPatientVerificationContainer
)`
  max-width: 330px;
  color: #333333;

  h2 {
    font-weight: 600;
    font-size: 1.875;
    margin-bottom: 2rem;
    letter-spacing: ${titleLetterSpacing};
    line-height: 130%;
  }

  h3,
  p {
    font-size: 1rem;
    letter-spacing: ${textLetterSpacing};
    line-height: 150%;
  }

  h3 {
    margin: 0 0 1rem 0;
    font-weight: 600;
    letter-spacing: ${titleLetterSpacing};
  }

  @media (min-width: 768px) {
    max-width: 600px;
    h2 {
      font-size: 2.25rem;
    }
  }
`

/**
 * Headers components
 */
const HeaderContainer = styled(DefaultHeaderContainer)`
  h2 {
    margin-bottom: 1.5rem;
    font-size: 1.4rem;
    font-weight: 600;
    letter-spacing: ${titleLetterSpacing};
    line-height: 130%;
  }

  p {
    margin-bottom: 2rem;
    font-size: 1rem;
    letter-spacing: ${textLetterSpacing};
    line-height: 150%;
  }

  @media (min-width: 768px) {
    h2 {
      font-size: 1.875rem;
    }
  }
`

/**
 * Layer components
 */
const Layer = styled(DefaultLayer)`
  background-color: ${colors.darkGrey};
  padding: 1rem;
`

const Modal = styled(DefaultModal)`
  box-shadow: 0 0.375rem 0.5rem -6px rgba(24, 39, 75, 0.12),
    0 0.5rem 1rem -6px rgba(24, 39, 75, 0.08);
  border-radius: 1rem;
`

/**
 * Upload components
 */
const UploadBoxContainer = styled(DefaultUploadBoxContainer)`
  background-color: #f9fafb;
  min-height: 11rem;
  border: 0.125rem dashed ${colors.gullGrey};
  border-radius: 32px;
  padding: 2rem;
`

const UploadButton = styled(DefaultUploadButton)`
  color: #72324a;
  font-weight: 600;
  letter-spacing: ${textLetterSpacing};
`

const UploadFileInfo = styled(DefaultUploadFileInfo)`
  p {
    letter-spacing: ${textLetterSpacing};
    line-height: 150%;
  }
`

const ImageCard = styled(DefaultImageCard)`
  margin-bottom: 2rem;
  height: 6.31rem;
  display: grid;
  border: 1px solid ${colors.athensGray};
  border-radius: 32px;
  box-shadow: 0 0.125rem 0.25rem -2px rgba(24, 39, 75, 0.12),
    0 0.25rem 0.25rem -2px rgba(24, 39, 75, 0.08);
`

const StyledDeleteButton = styled(DefaultStyledDeleteButton)`
  max-width: 2.25rem;
  padding-right: 1rem;
`

const ImageMetaInfo = styled(DefaultImageMetaInfo)`
  img {
    max-width: 5.875rem;
    padding-right: 1rem;
    padding-left: 1rem;
  }

  p {
    letter-spacing: ${textLetterSpacing};
    line-height: 150%;
  }
`

export const astellasCustomComponents: CustomerCustomComponents = {
  // Buttons
  InitialButton,
  NextButton,
  BackButton,
  ResendButton,
  TakePhotoButton,
  // Icons
  ContinueIcon,
  // Containers
  InfoContainer,
  InitialButtonContainer,
  ModalContainer,
  PatientUploadContainer,
  PatientVerificationContainer,
  // Headers
  HeaderContainer,
  // Modals
  Layer,
  Modal,
  // Upload section
  UploadBoxContainer,
  UploadButton,
  UploadFileInfo,
  ImageCard,
  StyledDeleteButton,
  ImageMetaInfo,
}
